import { CartPageColumn, CartPageRoot } from "@/components/cart/CartPageColumn";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { cn } from "@repo/design-system/utils";
import { memo } from "react";

/**
 * Loading skeleton for cart page. Mirrors the loaded page's header band and
 * column so the content does not shift when the data arrives.
 */
export const CartPageSkeleton = memo(function CartPageSkeleton() {
  return (
    <CartPageRoot className="min-h-full">
      <CartPageColumn className="pb-8 pt-4">
        {/* Checkout progress skeleton */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-28" />
        </div>

        {/* Header skeleton */}
        <div className="mb-4 mt-4 flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-5 w-32" />
        </div>

        {/* Main content skeleton */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Seller cart groups skeleton */}
          <div className="flex-1 space-y-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-gray-200 bg-white p-4"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="mb-2 h-5 w-40" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            ))}
          </div>

          {/* Summary skeleton - the same 380px rail as the loaded page's aside. */}
          <div className="lg:w-[380px] lg:flex-shrink-0">
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
              <div className="space-y-2 pt-4">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between pt-2">
                  <Skeleton className="h-5 w-12" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
              <Skeleton className="mt-4 h-12 w-full" />
            </div>
          </div>
        </div>
      </CartPageColumn>
    </CartPageRoot>
  );
});

/**
 * Error state for cart page
 */
export const CartPageError = memo(function CartPageError({
  error
}: {
  error: Error;
}) {
  // The server's message means nothing to a salon and can leak internals, so it
  // is reported instead of shown (routine 401/429 failures are filtered out).
  useReportLoadFailure(error, "cart page");

  return (
    <CartPageRoot className="min-h-full">
      <CartPageColumn className="py-16">
        <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <span className="text-2xl">!</span>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            Something went wrong
          </h1>
          <p className="mb-8 text-gray-600">
            We couldn&apos;t load your cart. Please try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={cn(
              "inline-flex items-center justify-center",
              "rounded-lg bg-purple-600 px-6 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700"
            )}
          >
            Refresh Page
          </button>
        </div>
      </CartPageColumn>
    </CartPageRoot>
  );
});
